/** LibraryPanel — therapist-wide reference document library. */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Chip } from "../../components/ui";
import { useLibraryApi } from "./useLibraryApi";
import type { LibraryDocument } from "./types";

const ACCEPT = ".pdf,.txt,.md";

const fmtSize = (n: number) =>
  n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

const LibraryPanel: React.FC = () => {
  const { t } = useTranslation();
  const api = useLibraryApi();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [items, setItems] = useState<LibraryDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LibraryDocument | null>(null);
  const [busyIds, setBusyIds] = useState<Record<string, boolean>>({});

  // One controller for the panel's lifetime (created by the mount effect,
  // aborted in its cleanup): cancels the initial load and every detached
  // processing poll on unmount. Same pattern as assessments' load effect.
  const abortRef = useRef<AbortController | null>(null);

  // Mirror of items for detached-poll rejection handling: lets a poll that
  // fails AFTER its document was deleted stay silent without depending on
  // render-scope state.
  const itemsRef = useRef<LibraryDocument[]>([]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  // Coded errors ("library.*" i18n keys) get translated; backend error
  // strings surface verbatim.
  const errText = useCallback((e: any, fallbackKey: string): string => {
    const msg = e?.message ? String(e.message) : "";
    if (msg.startsWith("library.")) return t(msg);
    return msg || t(fallbackKey);
  }, [t]);

  // Depend on the STABLE callback (api.list), never on the api object —
  // useLibraryApi returns a fresh object literal each render, and depending
  // on it would refire this effect forever (same pattern as assessments,
  // which depends on api.loadList).
  const listDocs = api.list;
  const refresh = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      setItems(await listDocs(signal));
      setError(null);
    } catch (e: any) {
      if (signal?.aborted) return; // unmounted — not an error
      console.error("[LibraryPanel] list:", e);
      setError(errText(e, "library.listFailed"));
    } finally {
      setLoading(false);
    }
  }, [listDocs, errText]);

  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;
    void refresh(ac.signal);
    return () => ac.abort();
  }, [refresh]);

  const patch = (docId: string, updater: (d: LibraryDocument) => LibraryDocument) =>
    setItems((prev) => prev.map((d) => (d.docId === docId ? updater(d) : d)));

  // Detached per-doc poll: the upload button is long since re-enabled; the
  // row chip shows progress. Rejections are ignored when the panel unmounted
  // (signal aborted) or the doc was deleted mid-processing — neither is an
  // error the user can act on.
  const trackProcessing = async (docId: string) => {
    const signal = abortRef.current?.signal;
    try {
      await api.waitForReady(docId, (fresh) => patch(docId, () => fresh), { signal });
    } catch (e: any) {
      if (signal?.aborted) return;
      if (!itemsRef.current.some((d) => d.docId === docId)) return;
      console.error("[LibraryPanel] waitForReady:", e);
      setError(errText(e, "library.stuckMessage"));
    }
  };

  const onPickFile = async (file: File) => {
    setUploading(true);
    setError(null);
    let docId: string;
    try {
      const title = file.name.replace(/\.[^.]+$/, "");
      docId = await api.uploadFile(file, title);
      await refresh(abortRef.current?.signal);
    } catch (e: any) {
      console.error("[LibraryPanel] upload:", e);
      setError(errText(e, "library.uploadFailed"));
      return;
    } finally {
      // Release the button as soon as the upload itself finished — a second
      // upload must work while this doc is still processing.
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
    void trackProcessing(docId);
  };

  const onDelete = async (doc: LibraryDocument) => {
    // Clear synchronously BEFORE any await: the dialog closes at once, so a
    // stale double-confirm cannot fire remove twice.
    setDeleteTarget(null);
    if (busyIds[doc.docId]) return;
    setBusyIds((m) => ({ ...m, [doc.docId]: true }));
    try {
      await api.remove(doc.docId);
      setItems((prev) => prev.filter((d) => d.docId !== doc.docId));
    } catch (e: any) {
      console.error("[LibraryPanel] delete:", e);
      setError(errText(e, "library.deleteFailed"));
    } finally {
      setBusyIds((m) => {
        const { [doc.docId]: _, ...rest } = m;
        return rest;
      });
    }
  };

  const onDownload = async (doc: LibraryDocument) => {
    try {
      const { downloadUrl } = await api.getItem(doc.docId);
      if (!downloadUrl) throw new Error("Malformed response: missing download_url");
      // window.open can be blocked by popup blockers (returns null); the repo
      // pattern is to use it anyway — the browser surfaces the blocker UI.
      window.open(downloadUrl, "_blank", "noopener");
    } catch (e: any) {
      console.error("[LibraryPanel] download:", e);
      setError(errText(e, "library.downloadFailed"));
    }
  };

  const statusChip = (doc: LibraryDocument) => {
    if (doc.status === "ready") return <Chip text={t("library.statusReady")} tone="ok" />;
    if (doc.status === "error") return <Chip text={t("library.statusError")} tone="bad" />;
    return <Chip text={t("library.statusProcessing")} tone="warn" />;
  };

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{t("library.title")}</h2>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? t("library.uploading") : t("library.uploadButton")}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPickFile(f); }}
        />
      </div>

      <p style={{ color: "var(--color-muted)", fontSize: 13, marginTop: 0 }}>
        {t("library.hint")}
      </p>

      {error && (
        <div role="alert" style={{ color: "var(--color-danger, #b3261e)", marginBottom: 10 }}>
          {error}
        </div>
      )}

      {loading && items.length === 0 && <div>{t("library.loading")}</div>}
      {!loading && items.length === 0 && <div>{t("library.empty")}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((doc) => (
          <div
            key={doc.docId}
            style={{
              display: "flex", alignItems: "center", gap: 10, padding: 10,
              border: "1px solid var(--color-border)", borderRadius: 10,
              background: "var(--color-surface)",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                {doc.title}
              </div>
              <div style={{ fontSize: 12, color: "var(--color-muted)" }}>
                {doc.filename} · {fmtSize(doc.size)} · {new Date(doc.createdAt).toLocaleDateString()}
                {doc.pageCount ? ` · ${doc.pageCount} ${t("library.pages")}` : ""}
              </div>
              {doc.status === "error" && doc.errorMessage && (
                <div style={{ fontSize: 12, color: "var(--color-danger, #b3261e)" }}>
                  {doc.errorMessage}
                </div>
              )}
            </div>
            {statusChip(doc)}
            <button disabled={!!busyIds[doc.docId]} onClick={() => void onDownload(doc)}>
              {t("library.download")}
            </button>
            <button disabled={!!busyIds[doc.docId]} onClick={() => setDeleteTarget(doc)}>
              {t("library.delete")}
            </button>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={deleteTarget != null}
        title={t("library.deleteConfirmTitle")}
        message={deleteTarget ? t("library.deleteConfirmBody", { title: deleteTarget.title }) : ""}
        variant="danger"
        confirmLabel={t("library.delete")}
        cancelLabel={t("library.cancel")}
        onConfirm={() => deleteTarget && void onDelete(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default LibraryPanel;
